import PAYMENT_METHODS from '~/server_shared/generated-types/PAYMENT_METHODS';
import PAYMENT_METHODS_DETAILS_DICT from '~/server_shared/generated-types/PAYMENT_METHODS_DETAILS_DICT';
import type { FundAccountRuleCreateParamsFundAccountRulePaymentMethodScope } from '~/server_shared/openapi/data-contracts';

/** The write-side value set, taken straight from the generated contract rather than
 *  re-declared — `FundAccountPaymentMethod` in `fund_accounts/schemas/rules.py` is a `Literal`,
 *  so spectree emits it as a union of string literals on the `values` array. */
export type FundAccountPaymentMethod = NonNullable<
  FundAccountRuleCreateParamsFundAccountRulePaymentMethodScope['values']
>[number];

/** Payment portals that own their own disbursement account, so the engine has no account to
 *  choose for them. The BE excludes exactly this pair; the pin below is what fails if the two
 *  sides ever disagree. */
const PORTAL_ROUTED_PAYMENT_METHODS = ['one_inc', 'commerce_bank'] as const;

type PortalRoutedPaymentMethod = (typeof PORTAL_ROUTED_PAYMENT_METHODS)[number];

/** Every method either generated dict knows about. Neither contains the other:
 *  `PAYMENT_METHODS_DETAILS_DICT` is the only one carrying `custom_method`, and `PAYMENT_METHODS`
 *  is the only one carrying `compensation`. Both are `as const`, so `keyof typeof` gives the
 *  literal keys rather than `string` — which is what lets the pin below say anything at all. */
type GeneratedPaymentMethod = keyof typeof PAYMENT_METHODS | keyof typeof PAYMENT_METHODS_DETAILS_DICT;

/** The set this screen offers: the generated union minus the portal-routed pair. Mirrors the
 *  union the BE builds in `fund_accounts/schemas/rules.py`. */
type DerivedScopeablePaymentMethod = Exclude<GeneratedPaymentMethod, PortalRoutedPaymentMethod>;

/** Compile-time pin for the direction nothing else covers: **every method the API accepts must be
 *  one we derive**. The opposite direction — every method we derive being one the API accepts —
 *  needs no assertion, because `FUND_ACCOUNT_PAYMENT_METHOD_OPTIONS` below is declared
 *  `FundAccountPaymentMethod` and fed from a `DerivedScopeablePaymentMethod[]` with no cast, so it
 *  already fails to compile. Only the *missing* method is invisible there: a shorter derived list
 *  stays assignable, and the UI silently drops an option the BE would accept.
 *
 *  The enforcement is the `A extends B` **constraint**, not the conditional body — an unreferenced
 *  type alias is still constraint-checked (TS2344), while the same alias without the constraint
 *  quietly resolves to `never` and reports nothing. Hence a declaration no code reads. */
type AssertExtends<A extends B, B> = A extends B ? true : never;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _EveryAcceptedMethodIsOffered = AssertExtends<FundAccountPaymentMethod, DerivedScopeablePaymentMethod>;

const ALL_PAYMENT_METHOD_KEYS: GeneratedPaymentMethod[] = [
  ...(Object.keys(PAYMENT_METHODS_DETAILS_DICT) as (keyof typeof PAYMENT_METHODS_DETAILS_DICT)[]),
  ...(Object.keys(PAYMENT_METHODS) as (keyof typeof PAYMENT_METHODS)[]),
];

const isScopeable = (key: GeneratedPaymentMethod): key is DerivedScopeablePaymentMethod =>
  !(PORTAL_ROUTED_PAYMENT_METHODS as readonly string[]).includes(key);

const SCOPEABLE_PAYMENT_METHOD_KEYS: DerivedScopeablePaymentMethod[] = Array.from(
  new Set(ALL_PAYMENT_METHOD_KEYS.filter(isScopeable))
);

/** A method's display name. `PAYMENT_METHODS` is the label dict; `custom_method` lives only in the
 *  details dict, under `display_name`. A key neither knows falls back to itself, so a stored scope
 *  holding a retired method still renders as something rather than blank — the read-side contract
 *  (`…PaymentMethodScopeInfo.values`) is a permissive `string[]` for exactly that reason. */
export const getPaymentMethodLabel = (value: string): string =>
  (PAYMENT_METHODS as Record<string, string | undefined>)[value] ??
  (PAYMENT_METHODS_DETAILS_DICT as Record<string, { display_name?: string } | undefined>)[value]?.display_name ??
  value;

export const FUND_ACCOUNT_PAYMENT_METHOD_OPTIONS: { value: FundAccountPaymentMethod; label: string }[] =
  SCOPEABLE_PAYMENT_METHOD_KEYS.map((value) => ({ value, label: getPaymentMethodLabel(value) })).sort((a, b) =>
    a.label.localeCompare(b.label)
  );
